/**
 * sqlite3mt — SHM correctness primitives
 *
 * Mechanical extraction from §96 §8.2 + §17.
 *
 * This module owns the two foundational SHM algorithms that every other module
 * depends on:
 *
 * - readWalSnapshotStable() — double-copy WAL snapshot reader (§8.2)
 * - xShmBarrier() — masked SHM push/pull barrier (§17, R12)
 * - allowedMask() — compute which SHM words a connection may push (§17)
 *
 * The SHM layout here follows SQLite's actual wal-index byte layout already
 * used elsewhere in the repo: two 48-byte WalIndexHdr copies followed by the
 * 40-byte checkpoint block and then the hash tables.
 */

import { SAB3_SHM_DATA_SIZE } from './sab-layout'
import type { WalSnapshot } from './types'

// ── §8.2 WAL header / checkpoint layout ────────────────────────

/** SQLite WalIndexHdr is 48 bytes = 12 32-bit words. */
export const WAL_INDEX_HDR_BYTES = 48
export const WAL_INDEX_HDR_WORDS = WAL_INDEX_HDR_BYTES / 4

/** Byte offset of the second wal-index header copy. */
export const WAL_INDEX_HDR_B_OFF = WAL_INDEX_HDR_BYTES

/** Checkpoint block (WalCkptInfo) starts after both header copies. */
export const WAL_CKPT_REGION_OFF = WAL_INDEX_HDR_BYTES * 2
export const WAL_CKPT_WORDS = 10
export const WAL_CKPT_BYTES = WAL_CKPT_WORDS * 4

/** Snapshot-relevant WalIndexHdr field indices (32-bit words). */
export const WAL_HDR_ICHANGE_WORD = 2
export const WAL_HDR_MXFRAME_WORD = 4
export const WAL_HDR_SALT0_WORD = 8
export const WAL_HDR_SALT1_WORD = 9

/** Snapshot-relevant WalCkptInfo field indices (32-bit words). */
export const WAL_CKPT_NBACKFILL_WORD = 0
export const WAL_CKPT_READMARK_WORD0 = 1
export const WAL_CKPT_NBACKFILL_ATTEMPTED_WORD = 8

const WAL_HEADER_REGION_WORDS = WAL_INDEX_HDR_WORDS * 2
const WAL_CKPT_WORD_BASE = WAL_CKPT_REGION_OFF / 4
const WAL_HASH_WORD_BASE = (WAL_CKPT_REGION_OFF + WAL_CKPT_BYTES) / 4
const DEFAULT_WAL_SNAPSHOT_STABLE_TIMEOUT_MS = 100

export interface ReadWalSnapshotStableOptions {
    timeoutMs?: number
}

export class WalSnapshotUnstableError extends Error {
    readonly timeoutMs: number
    readonly elapsedMs: number

    constructor(timeoutMs: number, elapsedMs: number) {
        super(`WAL snapshot did not stabilize within ${timeoutMs}ms`)
        this.name = 'WalSnapshotUnstableError'
        this.timeoutMs = timeoutMs
        this.elapsedMs = elapsedMs
    }
}

export function isWalSnapshotUnstableError(error: unknown): error is WalSnapshotUnstableError {
    return error instanceof WalSnapshotUnstableError
        || (error instanceof Error && error.name === 'WalSnapshotUnstableError')
}

function loadWord(words: Int32Array, index: number): number {
    return words.buffer instanceof SharedArrayBuffer
        ? Atomics.load(words, index) >>> 0
        : words[index]! >>> 0
}

function storeWord(words: Int32Array, index: number, value: number): void {
    if (words.buffer instanceof SharedArrayBuffer) {
        Atomics.store(words, index, value)
        return
    }

    words[index] = value
}

function copyWords(words: Int32Array, startWord: number, count: number): Uint32Array {
    const out = new Uint32Array(count)
    for (let index = 0; index < count; index += 1) {
        out[index] = loadWord(words, startWord + index)
    }
    return out
}

// ── §8.2 Stable WAL snapshot ────────────────────────────────────

/**
 * Read the checkpoint part of the wal-index (10 32-bit words).
 *
 * The normative spec names this helper copyCheckpointWords(); the "words" are
 * the 32-bit checkpoint fields, not raw bytes.
 */
export function copyCheckpointWords(
    shm: Int32Array,
    byteOffset: number = WAL_CKPT_REGION_OFF
): Uint32Array {
    return copyWords(shm, byteOffset / 4, WAL_CKPT_WORDS)
}

/**
 * Copy the 48-byte WalIndexHdr as 12 32-bit words.
 *
 * The helper keeps the spec's historic name even though the payload is the
 * 48-byte SQLite header, not 48 separate Int32 values.
 */
export function copyHeader48Words(
    shm: Int32Array,
    byteOffset: number
): Uint32Array {
    return copyWords(shm, byteOffset / 4, WAL_INDEX_HDR_WORDS)
}

/**
 * Check if two word arrays are element-wise equal.
 */
export function equalWords(
    a: ArrayLike<number>,
    b: ArrayLike<number>
): boolean {
    if (a.length !== b.length) return false
    for (let i = 0; i < a.length; i += 1) {
        if (a[i] !== b[i]) return false
    }
    return true
}

/**
 * Decode a WalSnapshot from a validated header copy + checkpoint copy.
 */
export function decodeSnapshot(
    hdr: ArrayLike<number>,
    ckpt: ArrayLike<number>
): WalSnapshot {
    return {
        salt0: hdr[WAL_HDR_SALT0_WORD] >>> 0,
        salt1: hdr[WAL_HDR_SALT1_WORD] >>> 0,
        iChange: hdr[WAL_HDR_ICHANGE_WORD] >>> 0,
        mxFrame: hdr[WAL_HDR_MXFRAME_WORD] >>> 0,
        nBackfill: ckpt[WAL_CKPT_NBACKFILL_WORD] >>> 0,
    }
}

/**
 * Basic sanity check for a decoded snapshot.
 *
 * The snapshot reader is used after bootstrap has primed SAB3, so the key
 * invariant here is that checkpoint progress never exceeds the live WAL tip.
 */
export function validSnapshot(s: WalSnapshot): boolean {
    return s.nBackfill <= s.mxFrame
}

function monotonicNowMs(): number {
    if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
        return performance.now()
    }
    return Date.now()
}

function normalizeSnapshotReadTimeoutMs(timeoutMs: number | undefined): number {
    if (timeoutMs == null) {
        return DEFAULT_WAL_SNAPSHOT_STABLE_TIMEOUT_MS
    }
    if (!Number.isFinite(timeoutMs) || timeoutMs < 0) {
        throw new RangeError(`WAL snapshot timeout must be a finite non-negative number, got ${timeoutMs}`)
    }
    return timeoutMs
}

function tryDecodeStableSnapshot(shmInt32: Int32Array): WalSnapshot | null {
    const A1 = copyHeader48Words(shmInt32, 0)
    const B1 = copyHeader48Words(shmInt32, WAL_INDEX_HDR_B_OFF)
    const C1 = copyCheckpointWords(shmInt32)

    const A2 = copyHeader48Words(shmInt32, 0)
    const B2 = copyHeader48Words(shmInt32, WAL_INDEX_HDR_B_OFF)
    const C2 = copyCheckpointWords(shmInt32)

    if (!equalWords(A1, B1)) return null
    if (!equalWords(A2, B2)) return null
    if (!equalWords(A1, A2)) return null
    if (!equalWords(C1, C2)) return null

    const snapshot = decodeSnapshot(A2, C2)
    return validSnapshot(snapshot) ? snapshot : null
}

/**
 * §8.2: Read a stable WAL snapshot from SAB3's wal-index region.
 *
 * The algorithm reads both header copies and the checkpoint block twice and
 * only accepts a snapshot when all double-copy comparisons stabilise.
 */
export function tryReadWalSnapshotStable(
    shmInt32: Int32Array,
    options: ReadWalSnapshotStableOptions = {}
): WalSnapshot | null {
    const timeoutMs = normalizeSnapshotReadTimeoutMs(options.timeoutMs)
    const startMs = monotonicNowMs()
    const deadlineMs = startMs + timeoutMs

    for (;;) {
        const snapshot = tryDecodeStableSnapshot(shmInt32)
        if (snapshot != null) {
            return snapshot
        }
        if (monotonicNowMs() >= deadlineMs) {
            return null
        }
    }
}

export function readWalSnapshotStable(
    shmInt32: Int32Array,
    options: ReadWalSnapshotStableOptions = {}
): WalSnapshot {
    const timeoutMs = normalizeSnapshotReadTimeoutMs(options.timeoutMs)
    const startMs = monotonicNowMs()
    const snapshot = tryReadWalSnapshotStable(shmInt32, { timeoutMs })
    if (snapshot != null) {
        return snapshot
    }
    throw new WalSnapshotUnstableError(timeoutMs, monotonicNowMs() - startMs)
}

// ── §17 xShmBarrier — dirty word tracking ──────────────────────

/**
 * Track which Int32 words in a SHM region are dirty relative to a
 * clean baseline. This is used by xShmBarrier to know which words
 * the WASM shadow has modified.
 */
export interface ShmDirtyTracker {
    readonly wordCount: number
    markDirty(wordIndex: number): void
    getDirtyIndices(): ReadonlySet<number>
    reset(): void
}

/**
 * Create a dirty-word tracker for a SHM region of the given word count.
 */
export function createShmDirtyTracker(wordCount: number): ShmDirtyTracker {
    const dirty = new Set<number>()
    return {
        get wordCount() { return wordCount },
        markDirty(wordIndex: number) {
            if (wordIndex < 0 || wordIndex >= wordCount) {
                throw new RangeError(`SHM dirty word index ${wordIndex} out of range [0,${wordCount})`)
            }
            dirty.add(wordIndex)
        },
        getDirtyIndices() { return dirty },
        reset() { dirty.clear() },
    }
}

// ── §17 allowedMask — which words may be pushed ─────────────────

/**
 * SQLite WAL lock ownership flags for a single connection.
 */
export interface ShmLockOwnership {
    holdsReadMarkUpdate: boolean
    readMarkSlot: number | null
    holdsCkpt: boolean
    holdsWriteOrRecover: boolean
}

function addWordIfInRange(mask: Set<number>, wordIndex: number, wordCount: number): void {
    if (wordIndex >= 0 && wordIndex < wordCount) {
        mask.add(wordIndex)
    }
}

function addWordRange(mask: Set<number>, start: number, endExclusive: number, wordCount: number): void {
    const from = Math.max(0, start)
    const to = Math.min(endExclusive, wordCount)
    for (let wordIndex = from; wordIndex < to; wordIndex += 1) {
        mask.add(wordIndex)
    }
}

/**
 * Compute which SHM words this connection may push back into SAB3.
 *
 * §17 / R12:
 * - read-mark update right → only that readMark word
 * - CKPT → nBackfill + nBackfillAttempted
 * - WRITE or RECOVER → both WalIndexHdr copies + hash tables
 *
 * The checkpoint block sits between the headers and the hash tables and is
 * intentionally excluded from the WRITE/RECOVER range unless CKPT/read-mark
 * ownership adds those specific words back in.
 */
export function allowedMask(
    ownership: ShmLockOwnership,
    wordCount: number = SAB3_SHM_DATA_SIZE / 4
): ReadonlySet<number> {
    const mask = new Set<number>()

    if (ownership.holdsReadMarkUpdate) {
        const slot = ownership.readMarkSlot
        if (slot == null || slot < 0 || slot > 4) {
            throw new RangeError(`readMarkSlot ${String(slot)} out of range [0,5)`)
        }
        addWordIfInRange(mask, WAL_CKPT_WORD_BASE + WAL_CKPT_READMARK_WORD0 + slot, wordCount)
    }

    if (ownership.holdsCkpt) {
        addWordIfInRange(mask, WAL_CKPT_WORD_BASE + WAL_CKPT_NBACKFILL_WORD, wordCount)
        addWordIfInRange(mask, WAL_CKPT_WORD_BASE + WAL_CKPT_NBACKFILL_ATTEMPTED_WORD, wordCount)
    }

    if (ownership.holdsWriteOrRecover) {
        addWordRange(mask, 0, WAL_HEADER_REGION_WORDS, wordCount)
        addWordRange(mask, WAL_HASH_WORD_BASE, wordCount, wordCount)
    }

    return mask
}

// ── §17 xShmBarrier — masked push, full pull ────────────────────

function assertBarrierShape(
    activeI32: Int32Array,
    cleanI32: Int32Array,
    sabI32: Int32Array
): void {
    if (cleanI32.length !== activeI32.length || sabI32.length !== activeI32.length) {
        throw new RangeError('xShmBarrier requires active, clean, and sab views to have identical lengths')
    }
}

/**
 * Execute the masked SHM barrier (§17, R12).
 */
export function xShmBarrier(
    activeI32: Int32Array,
    cleanI32: Int32Array,
    sabI32: Int32Array,
    dirtySet: ReadonlySet<number>,
    mask: ReadonlySet<number>
): void {
    assertBarrierShape(activeI32, cleanI32, sabI32)

    const len = activeI32.length

    for (const wordIndex of dirtySet) {
        if (wordIndex < 0 || wordIndex >= len) {
            throw new RangeError(`dirty word index ${wordIndex} out of range [0,${len})`)
        }
        if (mask.has(wordIndex)) {
            storeWord(sabI32, wordIndex, activeI32[wordIndex]!)
        }
    }

    for (let wordIndex = 0; wordIndex < len; wordIndex += 1) {
        const value = loadWord(sabI32, wordIndex)
        activeI32[wordIndex] = value
        cleanI32[wordIndex] = value
    }
}

// ── §17 xShmBarrier convenience wrapper ─────────────────────────

export interface ShmBarrierArgs {
    sabShmInt32: Int32Array
    activeInt32: Int32Array
    cleanInt32: Int32Array
    tracker: ShmDirtyTracker
    ownership: ShmLockOwnership
}

/**
 * Convenience wrapper that computes the mask and calls xShmBarrier.
 */
export function xShmBarrierWithTracker(args: ShmBarrierArgs): void {
    const mask = allowedMask(args.ownership, args.sabShmInt32.length)
    xShmBarrier(
        args.activeInt32,
        args.cleanInt32,
        args.sabShmInt32,
        args.tracker.getDirtyIndices(),
        mask
    )
    args.tracker.reset()
}

// ── SAB3 SHM view helper ────────────────────────────────────────

/**
 * Get an Int32Array view over the SHM data region of SAB3.
 */
export function sab3ShmInt32View(sab3: SharedArrayBuffer): Int32Array {
    return new Int32Array(sab3, 0, SAB3_SHM_DATA_SIZE / 4)
}
